import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import api, { onUnauthorized, type AuthUser } from '../lib/apiClient';

type AuthStatus = 'idle' | 'loading' | 'authenticated' | 'unauthenticated';

// Returned by login()/loginTwoFactor() when the account has 2FA enabled —
// no session has been issued yet in that case, `challenge` must be passed
// back to loginTwoFactor() to finish signing in.
export interface TwoFactorChallenge {
  two_factor_required: true;
  challenge: string;
}

interface LoginSuccess {
  token: string;
  user: AuthUser;
  two_factor_required?: false;
}

type LoginResponse = TwoFactorChallenge | LoginSuccess;

export interface AuthContextValue {
  user: AuthUser | null;
  roles: string[];
  permissions: string[];
  status: AuthStatus;
  login: (email: string, password: string) => Promise<TwoFactorChallenge | { two_factor_required?: false }>;
  loginTwoFactor: (challenge: string, code: string) => Promise<void>;
  loginWithToken: (token: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<AuthUser>;
  hasRole: (name: string) => boolean;
  hasPermission: (name: string) => boolean;
  can: (name: string) => boolean;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

function readStoredUser(): AuthUser | null {
  const stored = localStorage.getItem('user');
  if (!stored) return null;
  try {
    return JSON.parse(stored) as AuthUser;
  } catch {
    // Corrupted localStorage value — treat as logged out rather than crash.
    localStorage.removeItem('user');
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() => readStoredUser());
  const [status, setStatus] = useState<AuthStatus>(() =>
    localStorage.getItem('token') ? 'loading' : 'unauthenticated'
  );

  const storeSession = useCallback((token: string, sessionUser: AuthUser) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(sessionUser));
    setUser(sessionUser);
    setStatus('authenticated');
  }, []);

  const clearSession = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  // The cached user (roles/permissions) is only as fresh as the last
  // login/refresh — re-fetch it from the server on load so a role or
  // permission change made server-side takes effect without forcing a
  // logout, matching the source app's AuthContext.jsx behaviour.
  useEffect(() => {
    if (!localStorage.getItem('token')) return;

    let cancelled = false;
    api
      .get<AuthUser>('/me')
      .then(({ data }) => {
        if (cancelled) return;
        localStorage.setItem('user', JSON.stringify(data));
        setUser(data);
        setStatus('authenticated');
      })
      .catch(() => {
        // Token invalid/expired — the response interceptor already cleared
        // localStorage; just reflect that in state here.
        if (cancelled) return;
        setUser(null);
        setStatus('unauthenticated');
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Fires on any 401 from the API client, from anywhere in the app (not
  // just the initial /me check above) — keeps `status` in sync so
  // RequireAuth redirects to /login instead of the app silently looking
  // "logged in" against a session the server no longer honours.
  useEffect(() => onUnauthorized(clearSession), [clearSession]);

  // Returns the raw response so the caller (Login page) can detect a 2FA
  // challenge ({ two_factor_required: true, challenge }) instead of a real
  // session — in that case nothing is stored yet; call loginTwoFactor() to
  // finish.
  const login = useCallback(async (email: string, password: string) => {
    const { data } = await api.post<LoginResponse>('/login', { email, password });
    if (data.two_factor_required) return data;

    storeSession(data.token, data.user);
    return { two_factor_required: false as const };
  }, [storeSession]);

  const loginTwoFactor = useCallback(async (challenge: string, code: string) => {
    const { data } = await api.post<LoginSuccess>('/login/2fa', { challenge, code });
    storeSession(data.token, data.user);
  }, [storeSession]);

  // Used by the SSO callback page: the token already exists (minted by the
  // backend's Google/Microsoft callback and handed to us via ?token=), this
  // just stores it and fetches /me to populate `user`.
  const loginWithToken = useCallback(async (token: string) => {
    localStorage.setItem('token', token);
    try {
      const { data } = await api.get<AuthUser>('/me');
      localStorage.setItem('user', JSON.stringify(data));
      setUser(data);
      setStatus('authenticated');
    } catch (err) {
      clearSession();
      throw err;
    }
  }, [clearSession]);

  const logout = useCallback(async () => {
    try {
      await api.post('/logout');
    } catch {
      // Token may already be invalid; clear local state regardless.
    }
    clearSession();
  }, [clearSession]);

  // Re-syncs the cached user (name, photo, roles, ...) after the Account
  // page changes something about the current user — cheaper than a full
  // page reload and keeps the header avatar/name in sync immediately.
  const refreshUser = useCallback(async () => {
    const { data } = await api.get<AuthUser>('/me');
    localStorage.setItem('user', JSON.stringify(data));
    setUser(data);
    return data;
  }, []);

  // UI-gating conveniences only — never the source of truth. The backend
  // Gate/permission middleware is authoritative and every write endpoint
  // re-checks regardless of what the client believes here. `permissions`
  // already includes every permission for system_admin/admin (the backend
  // expands Gate::before's implicit "everything" into an explicit list —
  // see AuthController::profile()), so hasPermission() needs no special
  // case for those roles.
  const hasRole = useCallback((name: string) => user?.roles.includes(name) ?? false, [user]);
  const hasPermission = useCallback(
    (name: string) => user?.permissions.includes(name) ?? false,
    [user]
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      roles: user?.roles ?? [],
      permissions: user?.permissions ?? [],
      status,
      login,
      loginTwoFactor,
      loginWithToken,
      logout,
      refreshUser,
      hasRole,
      hasPermission,
      can: hasPermission,
    }),
    [user, status, login, loginTwoFactor, loginWithToken, logout, refreshUser, hasRole, hasPermission]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
